import { useMemo, useState } from 'react';

/**
 * useSortableData — click-to-sort state and comparator, previously reimplemented from scratch
 * (its own sortField/sortDir useState, its own handleSort toggle, its own SortIcon, its own
 * localeCompare) independently in at least 9 files. `field` is generic (string | number) rather
 * than a fixed union so callers with a mix of named fields and positional/extra columns (e.g.
 * RegisterItemList.tsx's sortable extra columns, referenced by index) can still use one hook.
 * Paired with components/SortableTh.tsx for the header cell markup.
 */
export function useSortableData<T, F extends string | number>(
  items: T[],
  valueFor: (item: T, field: F) => string | number,
  initialField: F,
  initialDir: 'asc' | 'desc' = 'asc',
) {
  const [field, setField] = useState<F>(initialField);
  const [dir, setDir] = useState<'asc' | 'desc'>(initialDir);

  const toggle = (next: F) => {
    if (next === field) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setField(next);
      setDir('asc');
    }
  };

  const sorted = useMemo(() => {
    const cmp = (a: T, b: T) => {
      const aVal = valueFor(a, field);
      const bVal = valueFor(b, field);
      if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
      return String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase(), undefined, { numeric: true, sensitivity: 'base' });
    };
    return [...items].sort((a, b) => (dir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, field, dir]);

  return { sorted, field, dir, toggle };
}
